import { useMemo } from 'react';

import { useTranslation } from '@/i18n';
import { StatItemType, StatItemVariant } from '@/models/globalModels';
import { useAppSelector } from '@/redux/hooks';

// Base items for stats banner - displayed on multiple pages as selected stats
export const useStatsItems = (): StatItemType[] => {
  const { t } = useTranslation('stats');
  const point_name = useAppSelector((state) => state.auth.companyAccount?.point_name);

  return useMemo(
    () => [
      // LOYALTY REWARDS
      {
        description: t('rewards.availableDescription'),
        icon: 'star',
        isLoyalty: true,
        isAdmin3: false,
        name: t('rewards.available'),
        pointsKey: 'loyalty_cash_available',
        type: 'rewards',
        variant: StatItemVariant.REWARDS_AVAILABLE,
        titleKey: 'cash_available_title',
      },
      {
        description: t('rewards.redeemedDescription'),
        icon: 'gift',
        isLoyalty: true,
        isAdmin3: false,
        name: t('rewards.redeemed'),
        pointsKey: 'loyalty_cash_redeemed',
        type: 'rewards',
        variant: StatItemVariant.REWARDS_REDEEMED,
        titleKey: 'cash_redeemed_title',
      },
      // POINTS
      {
        description: t('points.earnedDescription', { label: point_name }),
        icon: 'star',
        isLoyalty: true,
        isAdmin3: false,
        name: t('points.earned', { label: point_name }),
        pointsKey: 'loyalty_points_earned',
        type: 'points',
        variant: StatItemVariant.POINTS_EARNED,
        titleKey: 'points_earned_title',
      },
      {
        description: t('points.availableDescription', { label: point_name }),
        icon: 'star',
        isLoyalty: true,
        isAdmin3: false,
        name: t('points.available', { label: point_name }),
        pointsKey: 'loyalty_points_available',
        type: 'points',
        variant: StatItemVariant.POINTS_AVAILABLE,
        titleKey: 'points_available_title',
      },
      {
        description: t('points.redeemedDescription', { label: point_name }),
        icon: 'gift',
        isLoyalty: true,
        isAdmin3: false,
        name: t('points.redeemed', { label: point_name }),
        pointsKey: 'loyalty_points_redeemed',
        type: 'points',
        variant: StatItemVariant.POINTS_REDEEMED,
        titleKey: 'points_redeemed_title',
      },
      {
        description: t('points.expiredDescription', { label: point_name }),
        icon: 'cross',
        isLoyalty: true,
        isAdmin3: false,
        name: t('points.expired', { label: point_name }),
        pointsKey: 'loyalty_points_expired',
        type: 'points',
        variant: StatItemVariant.POINTS_EXPIRED,
        titleKey: 'points_expired_title',
      },
      {
        description: t('commission.earnedDescription'),
        icon: 'payouts',
        isAdmin3: false,
        moneyKey: 'total_money_earned',
        name: t('commission.earned'),
        pointsKey: 'total_points_earned',
        type: 'commission',
        variant: StatItemVariant.EARNED,
        dataFromStatsEnpoint: true,
        titleKey: 'earned_title',
      },
      {
        description: t('commission.pendingDescription'),
        icon: 'awaiting',
        isAdmin3: false,
        name: t('commission.pending'),
        type: 'commission',
        variant: StatItemVariant.AWAITING_APPROVE,
        dataFromStatsEnpoint: true,
        moneyKey: 'monetary_pending_sum',
        pointsKey: 'non_monetary_pending_sum',
        titleKey: 'awaiting_approval_title',
      },
      {
        description: t('commission.unpaidDescription'),
        icon: 'awaiting',
        isAdmin3: false,
        name: t('commission.unpaid'),
        type: 'commission',
        variant: StatItemVariant.AWAITING_PAYOUT,
        dataFromStatsEnpoint: true,
        moneyKey: 'commission_balance',
        pointsKey: 'points_balance',
        titleKey: 'awaiting_payment_title',
      },
      {
        description: t('commission.paidDescription'),
        icon: 'calendar',
        isAdmin3: false,
        name: t('commission.paid'),
        type: 'commission',
        variant: StatItemVariant.PAID,
        dataFromStatsEnpoint: true,
        moneyKey: 'total_money_paid',
        pointsKey: 'total_points_paid',
        titleKey: 'paid_out_so_far_title',
      },
      {
        description: t('commission.firstPurchaseDescription'),
        icon: 'payouts',
        isLoyalty: false,
        isAdmin3: false,
        moneyKey: 'total_first_purchase',
        name: t('commission.firstPurchase'),
        type: 'commission',
        variant: StatItemVariant.EARNED_FIRST_PURCHASE,
        dataFromStatsEnpoint: true,
        titleKey: 'show_referral_earned_first_event_text',
      },
      {
        description: t('commission.returningDescription'),
        icon: 'payouts',
        isLoyalty: false,
        isAdmin3: false,
        moneyKey: 'total_returning',
        name: t('commission.returningPurchase'),
        type: 'commission',
        variant: StatItemVariant.EARNED_RETURNING,
        dataFromStatsEnpoint: true,
        titleKey: 'show_referral_earned_second_event_text',
      },
      {
        description: t('cash.availableDescription'),
        icon: 'payouts',
        isAdmin3: false,
        moneyKey: 'loyalty_cash_available',
        name: t('cash.available'),
        type: 'cash',
        variant: StatItemVariant.LOYALTY_CASH_AVAILABLE,
        titleKey: 'cash_available_title',
      },
      {
        description: t('cash.redeemedDescription'),
        icon: 'gift',
        isAdmin3: false,
        moneyKey: 'loyalty_cash_redeemed',
        name: t('cash.redeemed'),
        type: 'cash',
        variant: StatItemVariant.LOYALTY_CASH_REDEEMED,
        titleKey: 'cash_redeemed_title',
      },
      {
        description: t('cash.expiredDescription'),
        icon: 'cross',
        isAdmin3: false,
        moneyKey: 'loyalty_cash_expired',
        name: t('cash.expired'),
        type: 'cash',
        variant: StatItemVariant.LOYALTY_CASH_EXPIRED,
        titleKey: 'cash_expired_title',
      },
      {
        description: t('cash.earnedDescription'),
        icon: 'payouts',
        isAdmin3: false,
        moneyKey: 'loyalty_cash_earned',
        name: t('cash.earned'),
        type: 'cash',
        variant: StatItemVariant.LOYALTY_CASH_EARNED,
        titleKey: 'cash_earned_title',
      },
      // REWARDS
      {
        description: t('rewards.cashBalanceDescription'),
        icon: 'payouts',
        isAdmin3: true,
        dataFromStatsEnpoint: true,
        moneyKey: 'rewards.rewards_cash_balance',
        name: t('rewards.cashBalance'),
        type: 'cash',
        variant: StatItemVariant.REWARDS_CASH_BALANCE,
        titleKey: 'rewards_cash_balance_title',
      },
      {
        description: t('rewards.pointsBalanceDescription', { label: point_name }),
        icon: 'star',
        isAdmin3: true,
        isLoyalty: true,
        dataFromStatsEnpoint: true,
        pointsKey: 'rewards.rewards_points_balance',
        name: t('rewards.pointsBalance', { label: point_name }),
        type: 'points',
        variant: StatItemVariant.REWARDS_POINTS_BALANCE,
        titleKey: 'rewards_points_balance_title',
      },
      {
        description: t('rewards.cashExpiredDescription'),
        icon: 'cross',
        isAdmin3: true,
        dataFromStatsEnpoint: true,
        moneyKey: 'rewards.rewards_cash_expired',
        name: t('rewards.cashExpired'),
        type: 'cash',
        variant: StatItemVariant.REWARDS_CASH_EXPIRED,
        titleKey: 'rewards_cash_expired_title',
      },
      {
        description: t('rewards.pointsExpiredDescription', { label: point_name }),
        icon: 'cross',
        isAdmin3: true,
        isLoyalty: true,
        dataFromStatsEnpoint: true,
        pointsKey: 'rewards.rewards_points_expired',
        name: t('rewards.pointsExpired', { label: point_name }),
        type: 'points',
        variant: StatItemVariant.REWARDS_POINTS_EXPIRED,
        titleKey: 'rewards_points_expired_title',
      },
      {
        description: t('rewards.cashFulfilledDescription'),
        icon: 'payouts',
        isAdmin3: true,
        dataFromStatsEnpoint: true,
        moneyKey: 'rewards.rewards_cash_fulfilled',
        name: t('rewards.cashFulfilled'),
        type: 'cash',
        variant: StatItemVariant.REWARDS_CASH_FULFILLED,
        titleKey: 'rewards_cash_fulfilled_title',
      },
      {
        description: t('rewards.pointsFulfilledDescription', { label: point_name }),
        icon: 'star',
        isAdmin3: true,
        isLoyalty: true,
        dataFromStatsEnpoint: true,
        pointsKey: 'rewards.rewards_points_fulfilled',
        name: t('rewards.pointsFulfilled', { label: point_name }),
        type: 'points',
        variant: StatItemVariant.REWARDS_POINTS_FULFILLED,
        titleKey: 'rewards_points_fulfilled_title',
      },
      {
        description: t('rewards.cashEarnedDescription'),
        icon: 'payouts',
        isAdmin3: true,
        dataFromStatsEnpoint: true,
        moneyKey: 'rewards.rewards_cash_earned',
        name: t('rewards.cashEarned'),
        type: 'cash',
        variant: StatItemVariant.REWARDS_CASH_EARNED,
        titleKey: 'rewards_cash_earned_title',
      },
      {
        description: t('rewards.pointsEarnedDescription', { label: point_name }),
        icon: 'star',
        isAdmin3: true,
        isLoyalty: true,
        dataFromStatsEnpoint: true,
        pointsKey: 'rewards.rewards_points_earned',
        name: t('rewards.pointsEarned', { label: point_name }),
        type: 'points',
        variant: StatItemVariant.REWARDS_POINTS_EARNED,
        titleKey: 'rewards_points_earned_title',
      },
      {
        description: t('rewards.cashRedeemedDescription'),
        icon: 'gift',
        isAdmin3: true,
        dataFromStatsEnpoint: true,
        moneyKey: 'rewards.rewards_cash_redeemed',
        name: t('rewards.cashRedeemed'),
        type: 'cash',
        variant: StatItemVariant.REWARDS_CASH_REDEEMED,
        titleKey: 'rewards_cash_redeemed_title',
      },

      {
        description: t('rewards.pointsRedeemedDescription', { label: point_name }),
        icon: 'gift',
        isAdmin3: true,
        isLoyalty: true,
        dataFromStatsEnpoint: true,
        pointsKey: 'rewards.rewards_points_redeemed',
        name: t('rewards.pointsRedeemed', { label: point_name }),
        type: 'points',
        variant: StatItemVariant.REWARDS_POINTS_REDEEMED,
        titleKey: 'rewards_points_redeemed_title',
      },
    ],
    [point_name, t],
  );
};
